import React, { useEffect } from 'react'

import { useApi } from '../classes/appApi'
import { UserModel, UserPermissionsModel } from '../defs/defs'
import eqxEventBroker, { eqxEvent } from '../shared/classes/eqxEventBroker'
import { useCache } from '../shared/components/CacheContext'
import EqxAlert from '../shared/components/EqxAlert/EqxAlert'
import EqxButton from '../shared/components/EqxButton/EqxButton'
import EqxContinuumLayout, {
    EqxContinuumFooterButton as FooterButton,
    EqxContinuumNavPageActionItem as PageActionItem,
    EqxContinuumSidebarItem as SidebarItem,
    EqxContinuumHeaderTopRightButton as TopRightButton,
    EqxContinuumHeaderTopRightMenu as TopRightMenu,
    EqxContinuumHeaderTopRightMenuItem as TopRightMenuItem
} from '../shared/components/EqxContinuumLayout/EqxContinuumLayout'
import { useFormState, useModalState } from '../shared/components/EqxFormModal'
import GlobalMessageContainer, { useGlobalMessages } from '../shared/components/EqxGlobalMessages/EqxGlobalMessages'
import EqxModal from '../shared/components/EqxModal/EqxModal'
import EqxRequestHandler from '../shared/components/EqxRequestHandler/EqxRequestHandler'
import EqxEnvironmentBanner from '../shared/components/EqxEnvironmentBanner/EqxEnvironmentBanner'
import useApiEndpoint from '../shared/hooks/ApiEndpoint'
import { useAuthContext } from './App/AppAuth'
import { clearPostAuthReturn } from './App/authReturnPath'
import { useDeploymentContext } from './App/AppDeploymentProvider'
import { useOffline } from './App/AppOfflineContext'
import { useTenantContext } from './App/AppTenant'
import HelpModal from './HelpModal'

interface MainLayoutContext {
    showHelpModal: (issue: string) => void
    messages: ReturnType<typeof useGlobalMessages>
    resetLayoutParams: (quietly: boolean) => void
    events: eqxEventBroker
    user?: UserModel
    permissions?: UserPermissionsModel
}
const mainLayoutContext = React.createContext<MainLayoutContext | null>(null)
const useMainLayoutContext = () => {
    const context = React.useContext(mainLayoutContext)
    if (!context) throw Error('useContext must be used within expected context')
    return context
}
const MainLayoutContextProvider = (props: { children: React.ReactNode; value: any }) => {
    return <mainLayoutContext.Provider value={props.value}>{props.children}</mainLayoutContext.Provider>
}
interface MainLayoutProps {
    children: React.ReactNode
    pageActions?: React.ReactNode
    pageTitle?: string | React.ReactNode
    containerType?: string
    relatedLinks?: { url: string; text: string }[]
}
function MainLayout(props: MainLayoutProps) {
    const auth = useAuthContext()
    const [dark, setDark] = React.useState<boolean>(false)
    const [showConfirmModal, setShowConfirmModal] = React.useState<
        { text?: string; yes: Function; no: Function } | undefined
    >()
    const { isOffline, isFileCached } = useOffline()
    const deployment = useDeploymentContext();
    React.useEffect(() => {
        if (dark) {
            document.documentElement.setAttribute('data-bs-theme', 'dark')
        } else {
            document.documentElement.setAttribute('data-bs-theme', 'light')
        }
    }, [dark])

    const confirmModal = (f: () => Promise<any>, text?: string) => {
        let resolveAction: Function
        let rejectAction: Function
        const promise = new Promise((resolve, reject) => {
            resolveAction = resolve
            rejectAction = reject
        }).then(f)
        setShowConfirmModal({ text, yes: resolveAction!, no: rejectAction! }) //lint doesn't pick up that resolveAction is defined in the promise
        return promise
    }
    const api = useApi()
    useEffect(() => {
        api.events.on('confirm-required', function (this: eqxEvent, callback: Function, message?: string) {
            this.setReturnValue(
                confirmModal(() => {
                    return callback({ force: true })
                }, message)
            )
        })
    }, [])

    const { cache, setCache } = useCache();
    const {
        response: layoutParams,
        loading: layoutParamsLoading,
        error: layoutParamsError,
        status: layoutParamsStatus,
        set: setLayoutParams,
        reset: resetLayoutParams,
    } = useApiEndpoint(api, api.endpoints.mainLayoutParams(), true, undefined, cache.mainLayoutParams);


    const tenant = useTenantContext();


    // Update cache when new layoutParams is fetched
    React.useEffect(() => {
        if (layoutParams && layoutParams !== cache.mainLayoutParams) {
            setCache('mainLayoutParams', layoutParams);
        }
    }, [layoutParams, setCache, cache.mainLayoutParams]);


    const pageTitle = props.pageTitle ?? 'Home'
    const containerType = props.containerType ?? 'md'
    const pageActions = props.pageActions ?? <></>
    const footerRelatedLinks = props.relatedLinks ?? [{ url: '/', text: 'Back to Home' }]

    const messages = useGlobalMessages()

    const helpForm = useFormState()
    const helpModal = useModalState()
    const showHelpModal = (issue: string) => {
        helpModal.show()
        helpForm.setValue('issue', issue)
    }
    const eventBroker = new eqxEventBroker('MainLayout')
    const layoutContext = {
        showHelpModal: showHelpModal,
        messages: messages,
        resetLayoutParams: (quietly: boolean) => {
            resetLayoutParams(quietly);
            setCache('mainLayoutParams', null);
        },
        events: eventBroker,
        user: layoutParams?.user,
        permissions: layoutParams?.permissions
    }

    const hasStaffID = layoutParams?.user?.staffID ? true : false
    const canAccessLMS = layoutParams?.permissions?.canAccessLMS
    const canClockInOut = layoutParams?.permissions?.canClockInOut
    const showEnvironmentBanner = !!deployment.environment && deployment.environment.toLowerCase() !== 'prod'

    return (
        <>
            {showEnvironmentBanner && (
                <EqxEnvironmentBanner
                    tenant={tenant.displayID}
                    environment={deployment.environment}
                    instance={deployment.instance}
                />
            )}
            <MainLayoutContextProvider value={layoutContext}>
                {isOffline && (
                    <EqxAlert variant="warning" icon="fas fa-wifi">
                        You are currently offline. Some features may be limited.
                    </EqxAlert>
                )}
                <EqxContinuumLayout
                pageTitle={pageTitle}
                containerType={containerType}
                pageActions={pageActions}
                dark={dark}
                logo={
                    <>
                        <img id="header-left-logo" className="header-logo" src="/images/logo-no-text.svg" alt="" />
                        <h1 id="header-left-logo-text-heading">
                            <img
                                id="header-left-logo-text"
                                className="header-logo"
                                src="/images/logo-text.svg"
                                alt="Continuum"
                            /> {' '}
                            <div id="header-left-tenant">{tenant.name}</div>
                        </h1>
                    </>
                }
                topRightMenu={
                    <>
                        <TopRightButton
                            name="help"
                            icon="fas fa-question-circle"
                            disabled={isOffline}
                        >
                            <TopRightMenu>
                                <TopRightMenuItem
                                    icon="fas fa-solid fa-circle-question"
                                    text="Help & Support"
                                    onClick={() => showHelpModal('help')}
                                />
                                <TopRightMenuItem
                                    icon="fas fa-solid fa-triangle-exclamation"
                                    text="Bug"
                                    onClick={() => showHelpModal('bug')}
                                />
                                <TopRightMenuItem
                                    icon="fas fa-solid fa-comment"
                                    text="Feedback"
                                    onClick={() => showHelpModal('feedback')}
                                />
                            </TopRightMenu>
                        </TopRightButton>
                        <TopRightButton name="user" icon="fas fa-user">
                            <div id="header-right-userinfo" className="clearfix">
                                <EqxRequestHandler
                                    state={{
                                        loading: layoutParamsLoading,
                                        error: layoutParamsError,
                                        hasContent: !!layoutParams?.user,
                                    }}
                                >
                                    <div id="header-right-userinfo-icon">
                                        <img src={layoutParams?.user.avatar} alt="" className="avatar" height="50" />
                                    </div>
                                    <div id="header-right-userinfo-name">{layoutParams?.user.name}</div>
                                    <div id="header-right-userinfo-username">{layoutParams?.user.email}</div>
                                </EqxRequestHandler>
                            </div>
                            <TopRightMenu>
                                {/*<TopRightMenuItem
                                    icon="fas fa-solid fa-circle-half-stroke"
                                    text={dark ? 'Disable Dark Mode' : 'Enable Dark Mode'}
                                    onClick={() => {
                                        setDark(!dark)
                                    }}
                                />*/}
                                <TopRightMenuItem
                                    icon="fas fa-right-left"
                                    text="Switch Organisation"
                                    onClick={() => {
                                        clearPostAuthReturn()
                                        window.location.assign('/switch?showSelector=1')
                                    }}
                                />
                                <TopRightMenuItem
                                    icon="fas fa-user"
                                    text="Sign Out"
                                    onClick={() => {
                                        if (window.confirm("Are you sure you want to sign out?")) {
                                            auth.logout()
                                        }
                                    }}
                                />
                            </TopRightMenu>
                        </TopRightButton>
                    </>
                }
                sidebar={
                    <>
                        <SidebarItem icon="fas fa-home" title="Home" url="/" />
                        <SidebarItem
                            icon="fas fa-file-alt"
                            title="Documents"
                            url="/controlled-documents"
                            disabled={isOffline && !isFileCached('/controlled-documents')}
                        />
                        <SidebarItem
                            icon="fas fa-file-contract"
                            title="Forms"
                            url="/forms"
                            disabled={isOffline && !isFileCached('/continuum-forms')}
                        />
                        {hasStaffID && (
                            <SidebarItem
                                icon="fas fa-trophy"
                                title="Training Records"
                                url="/training-records"
                                disabled={isOffline && !isFileCached('/training-records')}
                            />
                        )}
                        {tenant.isLMSEnabled && canAccessLMS && (
                            <SidebarItem
                                icon="fas fa-graduation-cap"
                                shortTitle="LMS"
                                title="Learning"
                                url="/lms"
                            />
                        )}
                        {hasStaffID && canClockInOut && (
                            <SidebarItem
                                icon="fas fa-clock"
                                title="Timelog"
                                url="/timelog"
                                disabled={isOffline && !isFileCached('/timelog/get-status')}
                            />
                        )}
                        <SidebarItem
                            icon="fas fa-tools"
                            title="Service Orders"
                            shortTitle="SOs"
                            url="/service-orders"
                            disabled={isOffline && (!isFileCached('/service-orders/today') || !isFileCached('/service-orders/future'))}
                        />
                        {hasStaffID && (
                            <SidebarItem
                                icon="fas fa-calendar-alt"
                                title="Absences"
                                url="/absences"
                                disabled={(isOffline && (!isFileCached('/absences/holiday-entitlements') || !isFileCached('/absences/unassigned'))) || !layoutParams?.permissions?.canBookAbsence}
                            />
                        )}
                        {tenant?.isManagedPayroll && hasStaffID &&
                            <SidebarItem
                                icon="fas fa-money-bill-wave"
                                title="Payslips"
                                url="/payslips"
                                disabled={(isOffline && !isFileCached('/payslips')) || !layoutParams?.permissions?.canViewPayslips}
                            />
                        }
                        {tenant?.isManagedPayroll && hasStaffID && (
                            <SidebarItem
                                icon="fas fa-plus-circle"
                                title="Expenses"
                                url="/expenses"
                                disabled={(isOffline && (!isFileCached('/additional-payments/pending') || !isFileCached('/additional-payments/processed'))) || !layoutParams?.permissions?.canAddExpenses}
                            />
                        )}
                    </>
                }
                footerContent={
                    <>
                        <FooterButton url="/" text="Overview" icon="fas fa-home" />
                        <FooterButton url="/work" text="Work" icon="fas fa-briefcase" />
                        <FooterButton url="/hr" text="HR" icon="fas fa-users" />
                        <FooterButton url="/docs" text="Docs" icon="fas fa-file-alt" />
                    </>
                }
                append={<GlobalMessageContainer messages={messages.messages} removeMessage={messages.removeMessage} />}
            >
                {props.children}
                <HelpModal modal={helpModal} form={helpForm} />

                    {showConfirmModal && (
                    <EqxModal
                        shown={true}
                        title={'Please Confirm'}
                        onHide={() => setShowConfirmModal(undefined)}
                        footer={
                            <>
                                <EqxButton
                                    text="Cancel"
                                    icon="fas fa-times"
                                    variant="danger"
                                    onClick={function () {
                                        showConfirmModal.no()
                                        setShowConfirmModal(undefined)
                                    }}
                                />
                                <EqxButton
                                    text="OK"
                                    icon="fas fa-check"
                                    variant="success"
                                    onClick={function () {
                                        showConfirmModal.yes()
                                        setShowConfirmModal(undefined)
                                    }}
                                />
                            </>
                        }
                    >
                        <p>{showConfirmModal.text ?? 'Are you sure you want to proceed?'}</p>
                    </EqxModal>
                    )}
                </EqxContinuumLayout>
            </MainLayoutContextProvider>
        </>
    )
}

export { MainLayout as default, PageActionItem, useMainLayoutContext }

